import { InputAdornment } from '@mui/material'
import { useRef, useState } from 'react'
import { Controller } from 'react-hook-form'

import {
  UButton,
  UCard,
  UIcon,
  UIconButton,
  ULink,
  UStack,
  UText,
  UFormControlInput,
  UAlert,
} from '@src/components'
import { PATHS } from '@src/consts'
import {
  TurnstileWidget,
  TurnstileWidgetHandle,
  SigninEmailMfa,
} from '@src/features/_components'
import { useScreenSize } from '@src/hooks'

import { useSigninForm, useSignin, useSigninEmailMfa } from './_hooks'
import { LoginPageState } from './type'

export const SigninMain = () => {
  const [loginPageState, setLoginPageState] =
    useState<LoginPageState>('INITIAL')
  const [showPassword, setShowPassword] = useState(false)
  const [loginErrors, setLoginErrors] = useState<string[]>([])
  // Bot protection for password login (CRD-4626)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const turnstileWidgetRef = useRef<TurnstileWidgetHandle>(null)

  const { signinFormObject } = useSigninForm()
  const { isUnderMdSize } = useScreenSize()

  function handleChangePageState(pageState: LoginPageState) {
    setLoginErrors([])
    setLoginPageState(pageState)
  }

  function handleTurnstileSuccess(token: string) {
    setTurnstileToken(token)
  }

  // Tokens are single-use and expire after ~300s: clear and re-challenge on
  // login failure, widget error, or token expiry.
  function handleResetTurnstile() {
    setTurnstileToken(null)
    turnstileWidgetRef.current?.reset()
  }

  function handleTurnstileScriptLoadError() {
    setLoginErrors([
      'セキュリティ確認の読み込みに失敗しました。ページを再読み込みしてください',
    ])
  }

  const {
    handleLoginWithEmailAndPassword,
    isLoadingLoginWithEmailAndPassword,
  } = useSignin({
    setLoginErrors,
    getSigninFormValue: signinFormObject.getValues,
    handleChangePageState,
    onLoginWithEmailAndPasswordError: handleResetTurnstile,
  })

  const { handleSubmitEmailMfa, isSubmittingEmailMfa } = useSigninEmailMfa({
    setLoginErrors,
  })

  return (
    <>
      {loginPageState === 'EMAIL_MFA' && (
        <UButton
          startIcon={<UIcon name="arrow_back" color="primary" />}
          color="primary"
          variant="text"
          className="mb-1"
          onClick={() => handleChangePageState('INITIAL')}
        >
          戻る
        </UButton>
      )}
      {loginErrors.length > 0 && (
        <UAlert severity="error" className="mb-2">
          {loginErrors.map((error) => (
            <UText key={error}>{error}</UText>
          ))}
        </UAlert>
      )}
      <UCard className={isUnderMdSize ? 'py-7' : 'py-7 px-6'}>
        {loginPageState === 'INITIAL' && (
          <form
            onSubmit={signinFormObject.handleSubmit(() =>
              handleLoginWithEmailAndPassword(turnstileToken)
            )}
          >
            <UStack spacing={3} alignItems="center">
              <Controller
                control={signinFormObject.control}
                name="email"
                render={({ field }) => (
                  <UFormControlInput
                    fullWidth
                    error={!!signinFormObject.formState.errors.email}
                    htmlFor="email"
                    required
                    uInputProps={{
                      ...field,
                      helperText:
                        signinFormObject.formState.errors.email?.message,
                      placeholder: 'メールアドレス',
                      InputProps: {
                        autoFocus: true,
                        startAdornment: (
                          <InputAdornment position="start">
                            <UIcon name="email" color="grayDark" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
              <Controller
                control={signinFormObject.control}
                name="password"
                render={({ field }) => (
                  <UFormControlInput
                    fullWidth
                    error={!!signinFormObject.formState.errors.password}
                    htmlFor="password"
                    required
                    uInputProps={{
                      ...field,
                      helperText:
                        signinFormObject.formState.errors.password?.message,
                      placeholder: 'パスワード',
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <UIcon name="lock" color="grayDark" />
                          </InputAdornment>
                        ),
                        type: showPassword ? 'text' : 'password',
                        endAdornment: (
                          <InputAdornment position="end">
                            <UIconButton
                              onClick={() => setShowPassword(!showPassword)}
                              iconProps={{
                                name: showPassword
                                  ? 'visibility_off'
                                  : 'visibility',
                                color: 'grayDark',
                              }}
                              variant="contained"
                            />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </UStack>
            <UStack spacing={3} alignItems="center" className="mt-5">
              <TurnstileWidget
                ref={turnstileWidgetRef}
                onSuccess={handleTurnstileSuccess}
                onError={handleResetTurnstile}
                onExpire={handleResetTurnstile}
                onScriptLoadError={handleTurnstileScriptLoadError}
              />
              <UButton
                size="large"
                color="primary"
                variant="contained"
                fullWidth
                type="submit"
                disabled={!turnstileToken}
                isLoading={isLoadingLoginWithEmailAndPassword}
              >
                ログイン
              </UButton>
              <ULink to={PATHS.RESET_PASSWORD} variant="body2" underline="none">
                <UText color="text.secondary" variant="body2">
                  パスワードをお忘れの方
                </UText>
              </ULink>
            </UStack>
          </form>
        )}
        {loginPageState === 'EMAIL_MFA' && (
          <SigninEmailMfa
            email={signinFormObject.getValues().email}
            isSubmitting={isSubmittingEmailMfa}
            onSubmit={handleSubmitEmailMfa}
          />
        )}
      </UCard>
    </>
  )
}
